import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCheckRole } from '@ospace/auth'
import { CampaignPage } from '@ospace/campaign/pages/CampaignPage/CampaignPage'
import { userTypes } from '@ospace/core-auth'
import { PageTitle } from '@ospace/layout'
import { ErrorBoundary } from '@ospace/shared'

import { useContactUpload } from '../hooks/useContactUpload'
import { useContactUploadStatus } from '../hooks/useContactUploadStatus'

type Props = {
  importId: number
  campaignId: number
}

const percentage = (a: number, b: number) => {
  if (b <= 0 || b < a) {
    return 0
  }

  return Math.round((a / b) * 100)
}

const DEFAULT_INTERVAL_SECONDS = 10
const MAX_WAIT_INTERVAL = 50
const WARNING_MESSAGE = 'No upload process have started.'

export const ContactUploadStatusPage = ({ importId, campaignId }: Props) => {
  const [intervalMs, setIntervalMs] = useState<number | false>(DEFAULT_INTERVAL_SECONDS * 1000)
  const [refetchingCounter, SetRefetchingCounter] = useState<number>(0)
  const { isLoading, isRefetching, data: status } = useContactUploadStatus(importId, intervalMs)
  const contactUpload = useContactUpload(importId)
  const navigate = useNavigate()

  useEffect(() => {
    if (status && status?.importSize > 0) {
      SetRefetchingCounter(0)
    }
    if ((status?.terminated && status.importSize > 0) || refetchingCounter > MAX_WAIT_INTERVAL) {
      setIntervalMs(false)
    }
  }, [isLoading, refetchingCounter, status])

  useEffect(() => {
    if (!contactUpload?.isLoading && !contactUpload?.data?.id) {
      setIntervalMs(false)
    }
  }, [contactUpload])

  useEffect(() => {
    if (isRefetching) {
      SetRefetchingCounter((r) => r + 1)
    }
  }, [isRefetching])

  return (
    <CampaignPage>
      <PageTitle>Upload Contacts Status</PageTitle>
      <div className='card' style={{ minHeight: 200 }}>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>
              Hubspot Contacts Upload: {contactUpload?.data?.fileName}
            </span>
          </h3>
        </div>
        <div
          style={{
            padding: '0 2.25rem',
            paddingTop: '1.5rem',
          }}
        >
          {!contactUpload?.isLoading && !contactUpload?.data?.id ? (
            <div>Import process #{importId} does not exist.</div>
          ) : (
            <>
              {isLoading || !status || status.importSize === 0 ? (
                <>
                  <div className='progress' style={{ height: 20 }}>
                    <div
                      className={`progress-bar bg-warning progress-bar-striped progress-bar-animated bg-primary`}
                      role='progressbar'
                      style={{ width: `100%`, fontWeight: 'bold' }}
                    >
                      Loading progress status
                    </div>
                  </div>
                  <div style={{ paddingTop: '1.5rem' }}>
                    {refetchingCounter > MAX_WAIT_INTERVAL && WARNING_MESSAGE}
                  </div>
                </>
              ) : (
                <>
                  <div className='progress' style={{ height: 20 }}>
                    {[
                      {
                        key: 'Succeed',
                        value: percentage(status.SUCCEEDED, status.importSize),
                        label: `${percentage(status.SUCCEEDED, status.importSize)}%`,
                        colour: 'bg-primary',
                      },
                      {
                        key: 'Failed',
                        value: percentage(status.FAILED, status.importSize),
                        label: `${percentage(status.FAILED, status.importSize)}%`,
                        colour: 'bg-danger',
                      },
                      {
                        key: 'In progress',
                        value: percentage(
                          status.importSize - (status.FAILED + status.SUCCEEDED),
                          status.importSize
                        ),
                        label: `${status.status}`,
                        colour: 'bg-secondary',
                        fontColour: 'black',
                      },
                    ].map(({ key, label, value, colour, fontColour }) => (
                      <div
                        key={key}
                        className={`progress-bar progress-bar${
                          !status?.terminated && '-striped'
                        } progress-bar-animated ${colour}`}
                        role='progressbar'
                        style={{ width: `${value}%`, fontWeight: 'bold', color: fontColour }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                  <div style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
                    <div className='d-flex flex-column content-justify-center w-100'>
                      {[
                        { label: 'Succeed', value: status.SUCCEEDED, colour: 'bg-primary' },
                        { label: 'Failed', value: status.FAILED, colour: 'bg-danger' },
                        { label: 'Total', value: status.importSize, colour: 'bg-secondary' },
                      ].map(({ label, value, colour }) => (
                        <div key={label} className='d-flex fs-6 fw-semibold align-items-center'>
                          <div className={`bullet w-8px h-6px rounded-2 ${colour} me-3`}></div>
                          <div className='text-gray-500 me-4'>{label}</div>
                          <div className='fw-bolder text-gray-700 text-xxl-end'>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {status.failedUploads.length > 0 && (
                    <button
                      style={{ marginBottom: '1.5rem' }}
                      type='submit'
                      className='btn btn-danger'
                      onClick={() => {
                        navigate(`/campaign/${campaignId}/contact/upload/${importId}/failed`, {
                          state: {
                            contacts: status.failedUploads,
                          },
                        })
                      }}
                    >
                      Show failed contacts
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </CampaignPage>
  )
}

const Controller = () => {
  useCheckRole([userTypes.Admin, userTypes.ClientManager, userTypes.CampaignManager])
  const params: any = useParams()
  return <ContactUploadStatusPage importId={params.importId} campaignId={params.id} />
}

export const ContactUploadStatusPageRouteHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)
